import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Badge,
  Progress,
} from "@chakra-ui/react";

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const MEDALS = ["🥇", "🥈", "🥉"];

// Top stocks ranked by expected annual dividend income (div_share × div_per_year × nr_shares)
const TopIncomeContributors = ({ stocks = [], limit = 5 }) => {
  const { top, totalIncome } = useMemo(() => {
    const withIncome = stocks.map((s) => ({
      ticker: s.ticker,
      company: s.company,
      income: Number(s.div_share) * Number(s.div_per_year) * Number(s.nr_shares),
    }));
    const totalIncome = withIncome.reduce((sum, s) => sum + s.income, 0);
    const top = withIncome
      .filter((s) => s.income > 0)
      .sort((a, b) => b.income - a.income)
      .slice(0, limit);
    return { top, totalIncome };
  }, [stocks, limit]);

  if (top.length === 0) return null;

  const max = top[0].income;
  const topShare = totalIncome > 0 ? (top.reduce((s, t) => s + t.income, 0) / totalIncome) * 100 : 0;

  return (
    <Card bg="gray.800" borderWidth="1px" borderColor="gray.700" boxShadow="lg" width="100%">
      <CardBody p={{ base: 3, md: 4 }}>
        <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Heading size="xs" color="teal.300">
            💵 Top Income Contributors
          </Heading>
          <Badge colorScheme="teal" fontSize="2xs" px={2} py={0.5} borderRadius="full">
            Top {top.length} = {topShare.toFixed(0)}% of income
          </Badge>
        </HStack>

        <VStack spacing={2.5} align="stretch">
          {top.map((s, i) => {
            const pctOfTotal = totalIncome > 0 ? (s.income / totalIncome) * 100 : 0;
            const barPct = max > 0 ? (s.income / max) * 100 : 0;
            return (
              <Box key={s.ticker}>
                <HStack justify="space-between" mb={1}>
                  <HStack spacing={2} minW={0}>
                    <Text fontSize="sm" w="22px" textAlign="center">
                      {MEDALS[i] || `${i + 1}.`}
                    </Text>
                    <Badge colorScheme="purple" fontSize="2xs" px={1.5} borderRadius="md">
                      {s.ticker}
                    </Badge>
                    <Text fontSize="xs" color="gray.400" noOfLines={1}>
                      {s.company}
                    </Text>
                  </HStack>
                  <HStack spacing={2} flexShrink={0}>
                    <Text fontSize="sm" fontWeight="bold" color="green.300">
                      {fmtMoney(s.income)}/yr
                    </Text>
                    <Text fontSize="2xs" color="gray.500" w="38px" textAlign="right">
                      {pctOfTotal.toFixed(1)}%
                    </Text>
                  </HStack>
                </HStack>
                <Progress
                  value={barPct}
                  size="xs"
                  colorScheme="green"
                  bg="whiteAlpha.100"
                  borderRadius="full"
                />
              </Box>
            );
          })}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default TopIncomeContributors;
